import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTrailDiagnostic, DiagnosticLevel } from "@/hooks/useTrailDiagnostic";
import { toast } from "@/hooks/use-toast";

type Category = "power_bi" | "sql" | "python";

interface Question {
  q: string;
  options: { label: string; weight: 0 | 1 | 2 }[];
}

const questionsByCategory: Record<Category, Question[]> = {
  power_bi: [
    {
      q: "Qual seu contato com Power BI hoje?",
      options: [
        { label: "Nunca abri o Power BI", weight: 0 },
        { label: "Já criei alguns relatórios simples", weight: 1 },
        { label: "Uso no dia a dia com DAX e modelagem", weight: 2 },
      ],
    },
    {
      q: "Você já escreveu medidas em DAX?",
      options: [
        { label: "Não sei o que é DAX", weight: 0 },
        { label: "Já usei CALCULATE e SUM", weight: 1 },
        { label: "Trabalho com contexto de filtro e time intelligence", weight: 2 },
      ],
    },
    {
      q: "Sobre modelagem de dados (relacionamentos, star schema)?",
      options: [
        { label: "Nunca ouvi falar", weight: 0 },
        { label: "Entendo o básico de relacionamentos 1:N", weight: 1 },
        { label: "Modelo star schema com fatos e dimensões", weight: 2 },
      ],
    },
  ],
  sql: [
    {
      q: "Como está seu SQL hoje?",
      options: [
        { label: "Nunca escrevi uma query", weight: 0 },
        { label: "Faço SELECT, WHERE e JOIN básico", weight: 1 },
        { label: "Uso window functions e CTEs", weight: 2 },
      ],
    },
    {
      q: "Qual comando você usaria para agrupar vendas por cliente?",
      options: [
        { label: "Não sei", weight: 0 },
        { label: "GROUP BY com SUM", weight: 2 },
        { label: "ORDER BY", weight: 0 },
      ],
    },
    {
      q: "Já otimizou uma query lenta?",
      options: [
        { label: "Não", weight: 0 },
        { label: "Já mexi com índices", weight: 1 },
        { label: "Analiso plano de execução", weight: 2 },
      ],
    },
  ],
  python: [
    {
      q: "Seu nível em Python para dados?",
      options: [
        { label: "Nunca programei", weight: 0 },
        { label: "Sei o básico da linguagem", weight: 1 },
        { label: "Uso pandas e numpy no dia a dia", weight: 2 },
      ],
    },
    {
      q: "Como você lê um CSV com pandas?",
      options: [
        { label: "Não sei", weight: 0 },
        { label: "pd.read_csv('arquivo.csv')", weight: 2 },
        { label: "open('arquivo.csv').read()", weight: 1 },
      ],
    },
    {
      q: "Já trabalhou com manipulação de DataFrames?",
      options: [
        { label: "Não", weight: 0 },
        { label: "Filtros e groupby", weight: 1 },
        { label: "Merge, pivot e apply customizado", weight: 2 },
      ],
    },
  ],
};

const categoryLabels: Record<Category, string> = {
  power_bi: "Power BI",
  sql: "SQL",
  python: "Python",
};

interface DiagnosticModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  category: Category;
  onComplete?: (level: DiagnosticLevel) => void;
}

export function DiagnosticModal({ open, onOpenChange, category, onComplete }: DiagnosticModalProps) {
  const questions = questionsByCategory[category];
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  const { save } = useTrailDiagnostic(category);

  const total = questions.length;
  const isLast = step === total - 1;
  const progress = ((step + (selected !== null ? 1 : 0)) / total) * 100;

  const reset = () => {
    setStep(0);
    setAnswers([]);
    setSelected(null);
  };

  const handleNext = async () => {
    if (selected === null) return;
    const newAnswers = [...answers, selected];

    if (!isLast) {
      setAnswers(newAnswers);
      setStep(step + 1);
      setSelected(null);
      return;
    }

    // Finalizar
    const score = newAnswers.reduce((sum, idx, i) => sum + questions[i].options[idx].weight, 0);
    const maxScore = total * 2;
    const pct = (score / maxScore) * 100;
    const level: DiagnosticLevel =
      pct >= 70 ? "avancado" : pct >= 35 ? "intermediario" : "iniciante";

    try {
      await save.mutateAsync({ level, score });
      toast({
        title: "Diagnóstico concluído!",
        description: `Nível sugerido: ${level.charAt(0).toUpperCase() + level.slice(1)}`,
      });
      onComplete?.(level);
      onOpenChange(false);
      reset();
    } catch (e) {
      toast({ title: "Erro ao salvar diagnóstico", variant: "destructive" });
    }
  };

  const question = questions[step];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <Badge variant="secondary" className="text-xs">
              Diagnóstico · {categoryLabels[category]}
            </Badge>
          </div>
          <DialogTitle>Antes de começar, conte pra gente…</DialogTitle>
          <DialogDescription>
            3 perguntas rápidas para sugerir o melhor ponto de partida na trilha.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Pergunta {step + 1} de {total}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-1.5" />
        </div>

        <div className="space-y-4 py-2">
          <h3 className="font-medium text-foreground">{question.q}</h3>
          <div className="space-y-2">
            {question.options.map((opt, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => setSelected(idx)}
                className={cn(
                  "w-full text-left p-3 rounded-lg border transition-all flex items-center gap-3",
                  selected === idx
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/40"
                )}
              >
                <div
                  className={cn(
                    "h-5 w-5 rounded-full border-2 flex items-center justify-center shrink-0",
                    selected === idx ? "border-primary bg-primary" : "border-muted-foreground/30"
                  )}
                >
                  {selected === idx && <CheckCircle2 className="h-3 w-3 text-primary-foreground" />}
                </div>
                <span className="text-sm">{opt.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Pular
          </Button>
          <Button onClick={handleNext} disabled={selected === null || save.isPending}>
            {isLast ? "Finalizar" : "Próxima"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
